<template>
  <div class="chat-widget">
    <!-- Floating button -->
    <button v-if="!isOpen" class="chat-fab" :class="{ 'chat-fab-compact': labelCollapsed }" @click="open">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
      </svg>
      <span class="chat-fab-label" :class="{ 'chat-fab-label-hidden': labelCollapsed }">Need help?</span>
    </button>

    <!-- Chat window -->
    <div v-if="isOpen" class="chat-window">
      <div class="chat-header">
        <span class="chat-title">{{ assistantName }}</span>
        <div class="chat-header-actions">
          <v-menu v-if="messages.length > 0" v-model="showMenu" location="bottom end" :z-index="10000">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon="more_vert" variant="text" size="small" class="chat-header-btn" />
            </template>
            <v-list density="compact">
              <v-list-item v-if="!escalated" @click="showEscalateConfirm = true">
                <v-list-item-title>Transfer to Support</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openSupportPage">
                <v-list-item-title>Submit a Ticket</v-list-item-title>
              </v-list-item>
              <v-list-item @click="endChat">
                <v-list-item-title>End chat</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn icon="close" variant="text" size="small" class="chat-header-btn" @click="close" />
        </div>
      </div>

      <div ref="messageArea" class="chat-messages" role="log" aria-live="polite" aria-label="Chat messages">
        <div v-if="messages.length === 0 && !loading" class="chat-empty">
          <p>Hi! I'm your AI assistant. How can I help you with DocuLocker today?</p>
        </div>

        <div v-for="msg in messages" :key="msg.id" class="chat-message"
             :class="msg.role === 'user' ? 'chat-message-user' : 'chat-message-ai'">
          <div>
            <div v-if="msg.role === 'ai'" class="chat-bubble" v-html="renderMarkdown(msg.text)"></div>
            <div v-else class="chat-bubble">{{ msg.text }}</div>
            <div v-if="msg.action && chatActions[msg.action]" class="chat-action-bar">
              <v-btn variant="tonal" size="small" color="primary" append-icon="open_in_new" @click="openAction(msg.action)">
                {{ chatActions[msg.action].label }}
              </v-btn>
            </div>
            <div class="chat-time">{{ msg.time }}</div>
          </div>
        </div>

        <div v-if="pendingResponse" class="chat-message chat-message-ai">
          <div class="chat-bubble chat-typing">
            <span class="dot"></span><span class="dot"></span><span class="dot"></span>
          </div>
        </div>

      </div>

      <v-overlay v-model="showEndConfirm" contained class="chat-confirm-overlay">
        <v-card class="chat-confirm-card">
          <v-card-text class="chat-confirm-text">Start a new conversation? This will clear the current chat history.</v-card-text>
          <v-card-actions class="justify-center">
            <v-btn variant="tonal" size="small" @click="cancelEndChat">Cancel</v-btn>
            <v-btn variant="flat" size="small" color="primary" @click="confirmEndChat">New conversation</v-btn>
          </v-card-actions>
        </v-card>
      </v-overlay>

      <div v-if="canEscalate && !showEscalateConfirm" class="chat-escalate-bar">
        <v-btn variant="text" size="small" color="primary" @click="showEscalateConfirm = true">Transfer to Support</v-btn>
      </div>

      <div v-if="showEscalateConfirm" class="chat-escalate-bar chat-escalate-confirm">
        <p class="chat-escalate-text">This will create a support ticket with your question. Our team will respond via email.</p>
        <div v-if="!spec.isAuthenticated" class="chat-escalate-form">
          <v-text-field ref="nameField" v-model="escalateName" variant="outlined" density="compact" placeholder="Your name" :rules="nameRules" validate-on="blur" class="mt-2 mb-4" />
          <v-text-field ref="emailField" v-model="escalateEmail" type="email" variant="outlined" density="compact" placeholder="Your email" :rules="emailRules" validate-on="blur" class="mb-4" />
        </div>
        <div class="chat-escalate-actions">
          <v-btn variant="tonal" size="small" @click="cancelEscalate">Cancel</v-btn>
          <v-btn variant="flat" size="small" color="primary" @click="escalate">Submit ticket</v-btn>
        </div>
      </div>

      <div class="chat-input-area">
        <v-textarea
          ref="messageInput"
          v-model="currentMessage"
          variant="outlined"
          density="compact"
          placeholder="Type a message..."
          rows="1"
          max-rows="4"
          auto-grow
          hide-details
          :maxlength="maxMessageLength"
          :disabled="pendingResponse"
          @keydown="handleKeydown"
        />
        <v-btn icon="send" color="primary" size="small" :disabled="!currentMessage.trim() || pendingResponse" @click="send" />
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'

var POLL_INTERVAL_MS = 1000
var POLL_RETRY_LIMIT = 30
var SESSION_KEY = 'dl_ai_chat_session_id'
var LABEL_DISPLAY_MS = 3000
var ESCALATION_THRESHOLD = 2
var GENERIC_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.'

var CHAT_ACTIONS = {
  one_time_secret: { label: 'Share a secret now' },
  your_locker: { label: 'Go to Your Locker' }
}

function getCsrfToken () {
  var meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

export default {
  props: {
    spec: { type: Object, required: true }
  },

  data: function () {
    return {
      isOpen: false,
      messages: [],
      currentMessage: '',
      sessionId: null,
      pendingResponse: false,
      loading: false,
      showEndConfirm: false,
      showMenu: false,
      maxMessageLength: 2000,
      assistantName: this.spec.assistantName || 'Chat Assistant',
      labelCollapsed: false,
      exchangeCount: 0,
      escalated: false,
      showEscalateConfirm: false,
      escalateName: '',
      escalateEmail: ''
    }
  },

  computed: {
    chatActions: function () {
      return CHAT_ACTIONS
    },
    canEscalate: function () {
      return this.exchangeCount >= ESCALATION_THRESHOLD && !this.escalated && !this.pendingResponse
    },
    nameRules: function () {
      return [
        function (v) { return !!v || "Your name can't be blank" }
      ]
    },
    emailRules: function () {
      return [
        function (v) { return !!v || "Email can't be blank" },
        function (v) { return !v || /.+@.+\..+/.test(v) || 'Email is not valid' }
      ]
    }
  },

  mounted: function () {
    this.sessionId = localStorage.getItem(SESSION_KEY) || null
    this._collapseTimer = setTimeout(function () {
      this.labelCollapsed = true
    }.bind(this), LABEL_DISPLAY_MS)
  },

  beforeUnmount: function () {
    clearTimeout(this._collapseTimer)
  },

  methods: {
    open: function () {
      this.isOpen = true
      this.labelCollapsed = true
      if (this.sessionId && this.messages.length === 0) {
        this.loadHistory()
      }
      this.$nextTick(function () {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus()
        }
      }.bind(this))
    },

    close: function () {
      this.isOpen = false
    },

    endChat: function () {
      this.showMenu = false
      this.showEndConfirm = true
    },

    confirmEndChat: function () {
      this.showEndConfirm = false
      localStorage.removeItem(SESSION_KEY)
      this.sessionId = null
      this.messages = []
      this.currentMessage = ''
      this.pendingResponse = false
      this.exchangeCount = 0
      this.escalated = false
      this.resetEscalateForm()
    },

    cancelEndChat: function () {
      this.showEndConfirm = false
    },

    openSupportPage: function () {
      this.showMenu = false
      if (this.spec.supportUrl) {
        window.open(this.spec.supportUrl, '_blank', 'noopener')
      }
    },

    openAction: function (actionKey) {
      var url = this.spec.actionUrls && this.spec.actionUrls[actionKey]
      if (url) {
        window.open(url, '_blank', 'noopener')
      }
    },

    cancelEscalate: function () {
      this.resetEscalateForm()
    },

    resetEscalateForm: function () {
      this.showEscalateConfirm = false
      this.escalateName = ''
      this.escalateEmail = ''
    },

    escalate: function () {
      var self = this

      if (!self.spec.isAuthenticated) {
        var nameResult = self.$refs.nameField.validate()
        var emailResult = self.$refs.emailField.validate()
        Promise.all([nameResult, emailResult]).then(function (results) {
          var allValid = results.every(function (r) { return r.length === 0 })
          if (allValid) {
            self.submitEscalation()
          }
        })
        return
      }

      self.submitEscalation()
    },

    submitEscalation: function () {
      var self = this
      var formData = new FormData()
      formData.append('session_id', self.sessionId)
      formData.append('authenticity_token', getCsrfToken())

      if (!self.spec.isAuthenticated) {
        formData.append('name', self.escalateName)
        formData.append('email', self.escalateEmail)
      }

      fetch(self.spec.escalateUrl, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      })
        .then(function (response) {
          if (response.ok) {
            self.resetEscalateForm()
            self.escalated = true
            self.messages.push({
              id: 'escalated-' + Date.now(),
              role: 'ai',
              text: 'Your question has been sent to our support team. We\'ll respond via email.',
              time: self.formatTime(new Date())
            })
            self.scrollToBottom()
          } else {
            throw new Error('Escalation failed')
          }
        })
        .catch(function () {
          self.showEscalateConfirm = false
          self.messages.push({
            id: 'escalate-error-' + Date.now(),
            role: 'ai',
            text: GENERIC_ERROR_MESSAGE,
            time: self.formatTime(new Date())
          })
          self.scrollToBottom()
        })
    },

    loadHistory: function () {
      var self = this
      self.loading = true

      fetch(self.spec.apiUrl + '?session_id=' + self.sessionId + '&format=json', {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })
        .then(function (response) { return response.json() })
        .then(function (data) {
          self.loading = false
          if (data.conversation_history && data.conversation_history.length > 0) {
            self.messages = []
            data.conversation_history.forEach(function (msg) {
              self.messages.push({ id: msg.id + '-user', role: 'user', text: msg.message, time: msg.time })
              if (msg.ai_response) {
                var aiMsg = { id: msg.id + '-ai', role: 'ai', text: msg.ai_response, time: msg.time }
                if (msg.action) { aiMsg.action = msg.action }
                self.messages.push(aiMsg)
                self.exchangeCount++
              }
            })
            self.escalated = data.escalated || false
            self.scrollToBottom()
          }
        })
        .catch(function () {
          self.loading = false
        })
    },

    send: function () {
      var message = this.currentMessage.trim()
      if (!message || this.pendingResponse) return

      this.messages.push({
        id: 'user-' + Date.now(),
        role: 'user',
        text: message,
        time: this.formatTime(new Date())
      })
      this.currentMessage = ''
      this.pendingResponse = true
      this.scrollToBottom()

      var self = this
      var formData = new FormData()
      formData.append('ai_chat_message[message]', message)
      if (self.sessionId) {
        formData.append('ai_chat_message[session_id]', self.sessionId)
      }
      formData.append('format', 'json')
      formData.append('authenticity_token', getCsrfToken())

      fetch(self.spec.apiUrl, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      })
        .then(function (response) { return response.json() })
        .then(function (data) {
          if (data.session_id) {
            self.sessionId = data.session_id
            localStorage.setItem(SESSION_KEY, self.sessionId)
          }

          if (data.message_id) {
            self.pollForResponse(data.message_id)
          } else {
            self.pendingResponse = false
          }
        })
        .catch(function () {
          self.pendingResponse = false
          self.messages.push({
            id: 'error-' + Date.now(),
            role: 'ai',
            text: GENERIC_ERROR_MESSAGE,
            time: self.formatTime(new Date())
          })
          self.scrollToBottom()
        })
    },

    pollForResponse: function (messageId) {
      var self = this
      var retryCount = 0

      var poll = function () {
        if (retryCount >= POLL_RETRY_LIMIT) {
          self.pendingResponse = false
          self.messages.push({
            id: 'timeout-' + Date.now(),
            role: 'ai',
            text: 'The response is taking longer than expected. Please try again.',
            time: self.formatTime(new Date())
          })
          self.scrollToBottom()
          return
        }

        retryCount++
        fetch(self.spec.apiUrl + '/' + messageId + '?format=json', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        })
          .then(function (response) { return response.json() })
          .then(function (data) {
            if (data.status === 'completed' || data.status === 'failed') {
              self.pendingResponse = false
              if (data.status === 'completed') {
                self.exchangeCount++
              }
              var responseText = data.ai_response || data.user_error_message || 'Sorry, I could not process your request.'
              var msg = {
                id: messageId + '-ai',
                role: 'ai',
                text: responseText,
                time: self.formatTime(new Date())
              }
              if (data.action) { msg.action = data.action }
              self.messages.push(msg)
              self.scrollToBottom()
            } else {
              setTimeout(poll, POLL_INTERVAL_MS)
            }
          })
          .catch(function () {
            self.pendingResponse = false
            self.messages.push({
              id: 'error-' + Date.now(),
              role: 'ai',
              text: GENERIC_ERROR_MESSAGE,
              time: self.formatTime(new Date())
            })
            self.scrollToBottom()
          })
      }

      setTimeout(poll, POLL_INTERVAL_MS)
    },

    handleKeydown: function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.send()
      }
    },

    formatTime: function (date) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    },

    renderMarkdown: function (text) {
      var prepared = this.splitSentencesIntoParagraphs(text || '')
      var html = Utils.format.markdown(prepared)
      var sanitized = DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })
      return sanitized.replace(/<a /g, '<a target="_blank" rel="noopener" ')
    },

    // Insert blank lines between sentences in prose paragraphs so each sentence
    // renders as its own <p>. The chat popup is narrow and long paragraphs feel cramped.
    // Only splits when the accumulated segment since the last break exceeds
    // SENTENCE_BREAK_MIN_CHARS — short fragments (abbreviations like "e.g.", "Mr.")
    // are left alone, which naturally avoids awkward mid-sentence breaks.
    // Skips lines that are list items, headings, blockquotes, or inside fenced code blocks.
    splitSentencesIntoParagraphs: function (text) {
      var SENTENCE_BREAK_MIN_CHARS = 80
      var lines = text.split('\n')
      var inFence = false
      var out = []
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i]
        if (/^\s*```/.test(line)) {
          inFence = !inFence
          out.push(line)
          continue
        }
        if (inFence || /^\s*([-*+]|\d+\.)\s/.test(line) || /^\s{4,}/.test(line) || /^\s*#/.test(line) || /^\s*>/.test(line) || line.trim() === '') {
          out.push(line)
          continue
        }
        var rebuilt = ''
        var segmentStart = 0
        var regex = /([.!?])\s+(?=[A-Z])/g
        var match
        while ((match = regex.exec(line)) !== null) {
          var endOfSentence = match.index + match[1].length
          var segment = line.slice(segmentStart, endOfSentence)
          if (segment.length >= SENTENCE_BREAK_MIN_CHARS) {
            rebuilt += segment + '\n\n'
            segmentStart = regex.lastIndex
          }
        }
        rebuilt += line.slice(segmentStart)
        out.push(rebuilt)
      }
      return out.join('\n')
    },

    scrollToBottom: function () {
      var self = this
      self.$nextTick(function () {
        var area = self.$refs.messageArea
        if (area) {
          area.scrollTop = area.scrollHeight
        }
      })
    }
  }
}
</script>

<style scoped>
.chat-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.chat-fab {
  height: 48px;
  padding: 0 20px 0 16px;
  border-radius: 24px;
  background-color: rgb(var(--v-theme-primary));
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s, border-radius 0.4s ease, padding 0.4s ease, gap 0.4s ease;
}

.chat-fab-compact {
  padding: 0 16px;
  border-radius: 50%;
  gap: 0;
}

.chat-fab-label {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
  max-width: 120px;
  overflow: hidden;
  opacity: 1;
  transition: max-width 0.4s ease, opacity 0.3s ease;
}

.chat-fab-label-hidden {
  max-width: 0;
  opacity: 0;
}

.chat-fab:hover {
  transform: scale(1.05);
}

.chat-window {
  position: relative;
  width: 360px;
  height: 500px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  background-color: rgb(var(--v-theme-primary));
  color: #fff;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.chat-title {
  font-size: 15px;
  font-weight: 600;
}

.chat-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chat-header .chat-header-btn {
  color: #fff;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
}

.chat-empty {
  text-align: center;
  color: #888;
  margin-top: 40px;
  font-size: 14px;
}

.chat-message {
  display: flex;
}

.chat-message-user {
  justify-content: flex-end;
}

.chat-message-ai {
  justify-content: flex-start;
}

.chat-time {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
  padding: 0 4px;
}

.chat-message-user .chat-time {
  text-align: right;
}

.chat-bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.45;
  word-wrap: break-word;
}

.chat-message-user .chat-bubble {
  background-color: rgb(var(--v-theme-primary));
  color: #fff;
  border-bottom-right-radius: 4px;
}

.chat-message-ai .chat-bubble {
  background-color: #f0f0f0;
  color: #333;
  border-bottom-left-radius: 4px;
}

.chat-message-ai .chat-bubble :deep(p) {
  margin: 0;
}

.chat-message-ai .chat-bubble :deep(p + p) {
  margin-top: 12px;
}

.chat-message-ai .chat-bubble :deep(a) {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}

.chat-message-ai .chat-bubble :deep(ul),
.chat-message-ai .chat-bubble :deep(ol) {
  margin: 4px 0;
  padding-left: 20px;
}

.chat-action-bar {
  margin-top: 6px;
}

.chat-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 18px;
}

.chat-typing .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #999;
  animation: typing 1.2s infinite;
}

.chat-typing .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.chat-typing .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}

.chat-escalate-bar {
  padding: 8px 12px;
  border-top: 1px solid #e0e0e0;
  text-align: center;
  flex-shrink: 0;
}

.chat-escalate-confirm {
  padding: 12px;
}

.chat-escalate-text {
  font-size: 13px;
  color: #555;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.chat-escalate-form {
  margin-bottom: 8px;
  text-align: left;
}

.chat-escalate-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.chat-input-area {
  display: flex;
  align-items: flex-end;
  padding: 8px 12px;
  border-top: 1px solid #e0e0e0;
  gap: 8px;
  flex-shrink: 0;
}

.chat-input-area .v-textarea {
  flex: 1;
}

.chat-confirm-card {
  margin: 16px;
  text-align: center;
}

.chat-confirm-text {
  font-size: 14px;
}
</style>
